import type {
  IAgentDetails,
  IAgentV2,
} from "@/core/interfaces/agent.interface";
import type { ICorporatePolicyPerson } from "@/core/interfaces/corporatePolicy.interface";
import { IPolicyOptionItem } from "../types/builder";
import { extractOptionItemsRaw } from "./optionSourceResolver";

type OrganizationUserItem = IAgentV2 | IAgentDetails;

function toOrganizationUser(user: OrganizationUserItem): IAgentV2 {
  return "organization_user" in user ? user.organization_user : user;
}

export const ASSIGNMENT_OPTION_SOURCE_KEYS = {
  USERS: "ASSIGNMENT_USERS",
  DEPARTMENTS: "ASSIGNMENT_DEPARTMENTS",
  EMPLOYEE_GRADES: "ASSIGNMENT_EMPLOYEE_GRADES",
  COST_CENTERS: "ASSIGNMENT_COST_CENTERS",
} as const;

export function filterOptionsBySearch(
  options: IPolicyOptionItem[],
  searchQuery?: string,
): IPolicyOptionItem[] {
  const normalizedQuery = searchQuery?.trim().toLowerCase();
  if (!normalizedQuery) {
    return options;
  }

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery),
  );
}

function resolvePolicyUserLabel(user: ICorporatePolicyPerson): string {
  const fullName = user.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const nameFromParts = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (nameFromParts) {
    return nameFromParts;
  }

  if (user.email?.trim()) {
    return user.email.trim();
  }

  return user.public_id ?? "—";
}

function resolveOrganizationUserLabel(user: IAgentV2): string {
  const profile = user.user;
  const fullName = profile?.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const nameFromParts = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (nameFromParts) {
    return nameFromParts;
  }

  if (profile?.email?.trim()) {
    return profile.email.trim();
  }

  return user.organization_user_id || "—";
}

export function mapOrganizationUsersToOptions(
  users: OrganizationUserItem[],
): IPolicyOptionItem[] {
  return users
    .map(toOrganizationUser)
    .filter((user) => !user.is_delete)
    .map((user): IPolicyOptionItem | null => {
      const value = user.organization_user_id?.trim();
      if (!value) {
        return null;
      }

      const option: IPolicyOptionItem = {
        label: resolveOrganizationUserLabel(user),
        value,
      };

      const email = user.user?.email?.trim();
      if (email && email !== option.label) {
        option.subtitle = email;
      }

      return option;
    })
    .filter((option): option is IPolicyOptionItem => option !== null);
}

export function mapPolicyUsersToOptions(
  users: ICorporatePolicyPerson[],
): IPolicyOptionItem[] {
  return users
    .map((user): IPolicyOptionItem | null => {
      const value = user.public_id ?? "";
      if (!value) {
        return null;
      }

      const option: IPolicyOptionItem = {
        label: resolvePolicyUserLabel(user),
        value,
      };

      if (user.email?.trim()) {
        option.subtitle = user.email.trim();
      }

      return option;
    })
    .filter((option): option is IPolicyOptionItem => option !== null);
}

const ORGANIZATION_GROUP_OPTION_KEYS = new Set<string>([
  ASSIGNMENT_OPTION_SOURCE_KEYS.DEPARTMENTS,
  ASSIGNMENT_OPTION_SOURCE_KEYS.EMPLOYEE_GRADES,
  ASSIGNMENT_OPTION_SOURCE_KEYS.COST_CENTERS,
]);

export function isOrganizationGroupOptionSource(sourceKey: string): boolean {
  return ORGANIZATION_GROUP_OPTION_KEYS.has(sourceKey);
}

export function formatNamedEntityLabel(
  name: string,
  code?: string | null,
): string {
  const trimmedName = name.trim();
  const trimmedCode = code?.trim() ?? "";
  if (trimmedName && trimmedCode) {
    return `${trimmedName} - ${trimmedCode}`;
  }
  return trimmedName || trimmedCode;
}

export function mapNamedEntitiesToOptions(
  entities: Array<{ name: string; public_id: string; code?: string }>,
): IPolicyOptionItem[] {
  return entities
    .map((entity) => ({
      label: formatNamedEntityLabel(entity.name ?? "", entity.code),
      value: entity.public_id,
    }))
    .filter((option) => option.value && option.label);
}

export function mapOrganizationGroupsToOptions(
  raw: unknown,
  optionSource: { labelField: string; valueField: string },
): IPolicyOptionItem[] {
  return extractOptionItemsRaw(raw)
    .map((item): IPolicyOptionItem | null => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const record = item as Record<string, unknown>;
      const name = String(record[optionSource.labelField] ?? "");
      const code = typeof record.code === "string" ? record.code : "";
      const value = String(
        record[optionSource.valueField] ?? record.public_id ?? "",
      ).trim();
      const label = formatNamedEntityLabel(name, code);

      if (!value || !label) {
        return null;
      }

      return { label, value };
    })
    .filter((option): option is IPolicyOptionItem => option !== null);
}
